import { inject, Injectable, OnDestroy } from '@angular/core';
import { assert } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { Camera } from 'three';
import { settingsStoreToken } from '../../../cross-team-shared/state/setting-state';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { ViewRegion } from '../../state/view-region-state';
import { MaterialService } from '../material.service';
import { SceneControllerService } from '../scene-controller.service';
import { AbstractMeshHelper } from './abstract-mesh-helper';

@Injectable()
export abstract class AbstractMeshService<T extends AbstractMeshHelper>
  extends InjectableSuperclass
  implements OnDestroy
{
  protected helpers = new Map<number, T>();

  protected sceneControllerService = inject(SceneControllerService);
  protected store = inject(PointCloudContentStore);
  protected settingsStore = inject(settingsStoreToken);
  protected renderStore = this.store('render');
  protected shellConnector = inject(shellConnectorToken);

  private materialService = inject(MaterialService);

  prepareToRender(viewRegion: ViewRegion, camera: Camera) {
    this.getHelpers().forEach((helper) => {
      const { areProjectionsEnabled } = this.settingsStore.state();
      helper.prepareToRender(
        {
          areProjectionsEnabled,
          camera,
          cameraLayer: viewRegion.layer,
          outputName: viewRegion.outputName,
          isUsingNewEditPanels:
            this.shellConnector.hasFeatureFlag('new-edit-panel'),
          areCuboidVerticesHidden: this.shellConnector.hasFeatureFlag(
            'hide-cuboid-vertices',
          ),
        },
        this.store.state(),
        this.materialService,
      );
    });
  }

  getHelpers() {
    return Array.from(this.helpers.values());
  }

  getHelper<U extends T = T>(index: number) {
    assert(this.helpers.has(index), 'Helper does not exist');
    return this.helpers.get(index) as U;
  }

  ngOnDestroy() {
    this.helpers.forEach((_, key) => {
      this.removeHelper(key);
    });
    this.helpers.clear();
  }

  protected addHelper(index: number, helper: T) {
    this.helpers.set(index, helper);
    this.sceneControllerService.addToScene(helper.getMeshes());
  }

  protected removeHelper(key: number) {
    const helper = this.helpers.get(key);
    if (helper) {
      this.sceneControllerService.removeFromScene(helper.getMeshes());
      this.helpers.delete(key);
      helper.dispose();
    }
  }
}
